import { useCallback } from "react";

import { useTranslations } from "@/shared/lang/useTranslations";

/**
 * "hace 5 min" for `last_used_at`, falling back to a plain date past thirty days.
 *
 * `null` in, `null` out — a key that has never been called must render as such and not as "hace un momento",
 * which is what a `?? new Date()` would produce.
 *
 * Twin of the same formatter inside Settings' API keys section; if a third consumer appears, hoist it.
 */
export function useLastUsedLabel() {
  const { t } = useTranslations("settings");

  return useCallback(
    (value: string | null | undefined): string | null => {
      if (!value) return null;
      const minutes = Math.floor((Date.now() - new Date(value).getTime()) / 60000);
      if (minutes < 1) return t("relative_time_now");
      if (minutes < 60) return t("relative_time_minutes", { n: minutes });
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return t("relative_time_hours", { n: hours });
      const days = Math.floor(hours / 24);
      if (days < 30) return t("relative_time_days", { n: days });
      return new Date(value).toLocaleDateString();
    },
    [t],
  );
}
